<script>
  import {getContext, onMount} from 'svelte';
  import {combineLatest} from 'rxjs';
  import {map} from 'rxjs/operators';
  import {PATHS} from '@/const/paths';
  import {LINKS} from '@/const';
  import {coinUtil} from '@/helper/coin';
  import {contractUtil} from '@/helper/contract';
  import {fixLink$} from '@/store/lang';
  import {isMobileSize$} from '@/streams/window-inner-size';
  import {contractMarkets$, spotMarkets$} from "@/modules/markets/streams-market";
  import {memeMarket$, memeQuery$} from '@/streams/config/MemeX';
  import {topGainers$} from "@/modules/markets/streams-ranking";
  import {numUtil} from '@/helper';
  import Button from '@/widgets/Button.svelte';
  import TrendImage from '@/modules/home/components/TrendImageNew.svelte';
  import SkeletonBasicView from '@/widgets/skeleton/BasicView.svelte';
  import SkeletonLine from '@/widgets/skeleton/SkeletonLine.svelte';
  import {marketUtil} from '@/modules/markets/util2';
  import {hotContracts$, hotSpots$} from '../streams';
  import lang from '../HomePage.lang';

  const HOT_DATA_LIST_COUNT = 7;
  const MORE_DATA_LIST_COUNT = 7;
  const LG = getContext('LG');
  const more = [
    {
      label: LG(lang.MMXTitle),
      cryptos: [],
      href: LINKS.PHEMEX_MEME_TRADE,
    },
  ];
  const TAB_TYPE = {
    SPOT: 'SPOT',
    CONTRACT: 'CONTRACT',
    NEW_LISTING: 'NEW_LISTING',
    GAINER: 'GAINER',
    MEMEX: 'MEMEX'
  };
  const marketsMap = {
    SPOT: {
      marketsId: 0,
      sortKey: 'combinationTurnoverEv',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname),
    },
    CONTRACT: {
      marketsId: 2,
      sortKey: 'combinationTurnoverEv',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname) + '?tabType=Contract',
    },
    NEW_LISTING: {
      marketsId: 1,
      sortKey: 'listTime',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname + '?tabType=NewListings'),
    },
    GAINER: {
      marketsId: 0,
      sortKey: 'changePercent',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname + '?tabType=Spot&sort=changePercent'),
    },
    MEMEX: {
      marketsId: 0,
      sortKey: 'priceChangePct24h',
      viewMoreLink: $fixLink$(PATHS.MARKETS.pathname + '?tabType=Spot&sort=changePercent'),
    },
  };

  let spotBackupList = [];
  let contractBackupList = [];

  const hotData$ = combineLatest([spotMarkets$, hotSpots$, contractMarkets$, hotContracts$, isMobileSize$, topGainers$, memeMarket$]).pipe(
    map(([spotMarkets, spots, contractMarkets, contracts, isMobileSize, topGainers, memeMarket]) => {
      let spotList = spots.map((s) => spotMarkets.find((m) => m.symbol === s)).filter(Boolean);
      let contractList = contracts.map((s) => contractMarkets.find((m) => m.symbol === s)).filter(Boolean);
      if (spotList.length < HOT_DATA_LIST_COUNT) {
        spotList = composeArray(spotList, spotBackupList).slice(0, HOT_DATA_LIST_COUNT);
      }
      if (contractList.length < HOT_DATA_LIST_COUNT) {
        contractList = composeArray(contractList, contractBackupList).slice(0, HOT_DATA_LIST_COUNT);
      }
      const newListingMarkets = marketUtil.sortWithListTime([...contractMarkets, ...spotMarkets]);
      if (isMobileSize) {
        return {
          [TAB_TYPE.SPOT]: spotList,
          [TAB_TYPE.CONTRACT]: contractList,
          [TAB_TYPE.GAINER]: topGainers,
          [TAB_TYPE.NEW_LISTING]: newListingMarkets.slice(0, HOT_DATA_LIST_COUNT),
          [TAB_TYPE.MEMEX]: memeMarket.slice(0, MORE_DATA_LIST_COUNT),
        };
      }
      return {
        [TAB_TYPE.SPOT]: spotList,
        [TAB_TYPE.CONTRACT]: contractList,
        [TAB_TYPE.NEW_LISTING]: newListingMarkets.slice(0, HOT_DATA_LIST_COUNT),
      };
    })
  );

  let activeTab = TAB_TYPE.CONTRACT;

  $: spotBackupList = genData(HOT_DATA_LIST_COUNT, TAB_TYPE.SPOT, [...$spotMarkets$]);
  $: contractBackupList = genData(HOT_DATA_LIST_COUNT, TAB_TYPE.CONTRACT, [...$contractMarkets$]);
  $: updateMore($memeMarket$);
  $: isMemex = activeTab === TAB_TYPE.MEMEX

  onMount(() => {
    // 赋值mmx的参数，获取 mmx/Gainers 列表
    memeQuery$.next({
      orderBy: "Gainers",
      pageNum: 1,
      // 取大，主要给header mmx搜索拿全量数据使用
      pageSize: 10
    })
  })

  function updateMore(memeMarket = []) {
    more[0].cryptos = memeMarket.slice(0, MORE_DATA_LIST_COUNT);
  }

  function composeArray(list1, list2) {
    const ret = [];
    [...list1, ...list2].forEach((item) => {
      if (!ret.find(({symbol}) => item.symbol === symbol)) {
        ret.push(item);
      }
    });
    return ret;
  }

  function genData(length, tab, ...rest) {
    const map = marketsMap[tab];
    const markets = rest.length > 1 ? rest[map.marketsId] : rest[0];
    return markets ? markets.sort((a, b) => b[map.sortKey] - a[map.sortKey]).slice(0, length) : [];
  }

  function tabClickHandler(event) {
    const tab = event.target.dataset.tab;
    if (!tab) return;
    activeTab = tab;
  }

  function tradeClickHandler(data) {
    return function () {
      window.open(data.tradeLink, '_blank', 'noopener');
    };
  }

  function viewMoreClickHandler() {
    window.open(marketsMap[activeTab].viewMoreLink, '_self', 'noopener');
  }
</script>

<style>
  .outer {
    margin-top: 60px;
  }
  .wrap {
    margin: 0 auto;
    width: 1200px;
    box-sizing: border-box;
  }
  .caption {
    margin-top: 0;
  }
  .content {
    gap: 20px;
  }
  .border-base {
    border: 1px solid #454952;
    border-radius: 20px;
  }
  .tab {
    margin-top: 0;
    margin-bottom: 0;
    line-height: 24px;
    color: #888B94;
    transition: color 0.2s ease-in-out;
  }
  .tab.active {
    color: #fff;
  }
  :global(.trade) {
    background: transparent !important;
    color: #fff !important;
    font-size: 14px !important;
    font-weight: 500 !important;
    border-color: #454952 !important;
  }
  :global(.trade:hover) {
    opacity: 0.8;
  }
  .container {
    /*display: grid;*/
    /*grid-template-columns: auto max-content max-content max-content max-content;*/
  }
  .row {
    height: 64px;
  }
  .row:hover,
  .crypto:hover {
    background: rgba(255,255,255,0.05);
  }
  .cell {
    padding-left: 40px;
  }
  .cell:first-child {
    padding-left: 24px;
  }
  .cell:last-child {
    padding-right: 24px;
  }
  .up {
    color: #32F469;
  }
  .down {
    color: #f61a14;
  }
  .iconfont {
    transform: rotate(270deg) translateX(-1px);
  }
  .right {
    width: 382px;
  }
  .more-wrap + :global(.more-wrap) {
    border-top: 1px solid #edeff3;
  }
  .more-wrap .title-wrap {
    height: 38px;
  }
  .more-wrap .title-wrap a i {
    color: #888B94;
  }
  .crypto {
    height: 64px;
    transition: all 0.3s;
  }
  .crypto:last-child {
    margin-bottom: 0;
  }
  .label {
    margin: 0;
  }
  .view-more-btn-wrap {
    margin-right: -32px;
  }
  .view-more-btn-wrap :global(button) {
    line-height: 38px;
    height: 38px;
    border-width: 0;
    color: #888B94;
  }
  .cell-pair {
    width: 140px;
  }
  .c-gray {
    color: #888B94
  }
  .table-header {
    margin-bottom: 12px;
  }
  .cell-110 {
    width: 110px;
  }
  .cell-124 {
    width: 124px;
  }
  .lh34 {
    line-height: 34px;
  }
  .cell-change-percent {
    width: 90px;
  }
  .img-wrap {
    width: 32px;
    height: 32px;
    background: #2c2c2c;
    border-radius: 50%;
  }
  .img-wrap > img {
    object-fit: contain;
  }
  @media screen and (max-width: 640px) {
    .outer {
      margin-top: 40px;
    }
    .wrap {
      padding: 0 16px;
      width: 100%;
    }
    .caption {
      font-size: 18px;
      margin-bottom: 0;
    }
    .content {
      border: 0;
      margin-top: 20px;
      gap: 0;
    }
    .title-wrap {
      padding: 0 20px;
    }
    .data-wrap {
      flex: 1;
      width: 100%;
      padding: 20px 0;
    }
    .table-header {
      display: none;
      padding: 0 20px;
    }
    .tab {
      flex: 0 0 auto;
      margin-left: 24px;
      font-size: 14px;
      color: #777b83;
    }
    .tab:first-child {
      margin-left: 0;
    }
    .tab.active {
      color: #fff;
    }
    .tab-wrap {
      overflow: auto;
    }
    .tab-wrap::-webkit-scrollbar {
      width: 0;
    }
    .row {
      justify-content: space-between;
      padding: 0;
      height: 60px;
    }
    .container {
      border: none;
      padding: 0 20px;
      width: unset;
      grid-template-columns: auto auto auto;
      margin: 12px auto 0 auto;
    }
    .cell-val-pair {
      text-align: left;
      min-width: 170px;
    }
    .cell-val-pair img {
      width: 24px;
      height: 24px;
    }
    .img-wrap {
      width: 24px;
      height: 24px;
    }
    .cell-val-pair-mmx {
      min-width: 160px;
    }
    .cell-val-pair-mmx img {
      border-radius: 50%;
      margin-right: 0;
    }
    .cell-val-close {
      text-align: left;
    }
    .cell-val-changepercent {
      text-align: right;
    }
    .row .cell {
      padding-left: 0;
    }
    .cell:first-child {
      font-size: 14px;
    }
    .cell:nth-child(2),
    .cell:nth-child(3) {
      font-size: 16px;
    }
    .row .cell:nth-child(4),
    .row .cell:last-child {
      display: none;
    }
    .cell-type {
      color: var(--T5);
    }
    .data-wrap :global(.h5-more) {
      margin: 0 auto;
    }
    .data-wrap :global(.h5-more button) {
      margin: 0 auto;
      height: 28px;
      line-height: 28px;
    }
    .skeleton-group {
      padding: 0 20px;
      margin: 20px 0 0;
      box-sizing: border-box;
    }
    :global(.skeleton-group .skeleton-line) {
      margin-top: 0;
      height: 60px;
      gap: 12px;
    }
  }
</style>

<section class="outer">
  <div class="wrap TW">
    <h2 class="caption fw3 f32 tc">{LG(lang.SeizeNextBigTrade)}</h2>
    <div class="content df br12 fw2 mt32">
      <div class="border-base data-wrap f1 pv24">
        <div class="title-wrap df aic jcsb ph24">
          <div class="tab-wrap df aic" on:click={tabClickHandler}>
            <h3 class="tab f16 cp" class:active={activeTab === TAB_TYPE.CONTRACT} data-tab={TAB_TYPE.CONTRACT}>{LG(lang.HotContract)}</h3>
            <h3 class="tab ml24 f16 cp" class:active={activeTab === TAB_TYPE.SPOT} data-tab={TAB_TYPE.SPOT}>{LG(lang.HotSpot)}</h3>
            <h3 class="tab ml24 f16 cp" class:active={activeTab === TAB_TYPE.NEW_LISTING} data-tab={TAB_TYPE.NEW_LISTING}>{LG(lang.NewListhings)}</h3>
            <h3 class="tab ml24 f16 cp dn xs:db" class:active={activeTab === TAB_TYPE.MEMEX} data-tab={TAB_TYPE.MEMEX}>{LG(lang.MMXTitle)}</h3>
          </div>
          <div class="view-more-btn-wrap c-gray f14 xs:dn">
            <Button type="text" on:click={viewMoreClickHandler}><i class="iconfont arrow dib fw3">&#xe623;</i></Button>
          </div>
        </div>
        <SkeletonBasicView loaded={$hotData$ && $hotData$[activeTab] && $hotData$[activeTab].length === HOT_DATA_LIST_COUNT}>
          <svelte:fragment slot="skeleton">
            <div class="df fdcr ml24 mr24 mr12 skeleton-group">
              {#each new Array(HOT_DATA_LIST_COUNT).fill(0) as item}
                <SkeletonLine class="mt12" height="64px" childProps={!$isMobileSize$ ? [
                  {width: '32px', height: '32px', borderRadius: '50%', baseColor: '#333', highlightColor: '#090a0a'},
                  {width: '180px', baseColor: '#333', highlightColor: '#090a0a'},
                  {width: '120px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
                  {width: '60px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
                  {width: '100px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
                  {width: '70px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
                ] : [
                  {width: '140px', height: '25px', borderRadius: '4px', baseColor: '#333', highlightColor: '#090a0a'},
                  {width: '80px',  height: '25px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
                  {width: '70px',  height: '25px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
                ]}></SkeletonLine>
              {/each}
            </div>
          </svelte:fragment>
          <div class="df jcsb c-gray fw1 f12 ph20 mt12 table-header">
            <span class="f1">{LG(lang.TradingPairs)}</span>
            <span class="cell-124">{LG(lang.LastPrice)}</span>
            <span class="cell-124">{LG(lang.Change24H)}</span>
            <span class="cell-124">{LG(lang.Chart)}</span>
            <span class="cell-110 tr">{LG(lang.Action)}</span>
          </div>
          <div class="container">
            {#if $hotData$ && $hotData$[activeTab]}
              {#each $hotData$[activeTab] as data}
                {#if isMemex}
                  <div class="row df aic jcsb ph24 cp" on:click={tradeClickHandler(data)}>
                    <div class="df aic jcfs fw2 f1 lh34 cell-val-pair-mmx">
                      <div class="img-wrap mr12">
                        <img width="32" height="32" src={data.logoUrl || ''} onerror="this.src='https://img.phemex.com/v1/a4afa886-3400-4962-859d-bba075cc0570/defult-coin.svg'" alt={data.fullNamefullName} />
                      </div>
                      <div class="f14 fw2 ml12">{data.coin}</div>
                    </div>
                    <div class="f1 tl lh34">${numUtil.delimit(data.price)}</div>
                    <div class="lh34 tr cell-change-percent" class:up={Number(data.priceChangePct24h) > 0} class:down={Number(data.priceChangePct24h) < 0} >
                      {`${(Number(data.priceChangePct24h) > 0 && '+') || (Number(data.priceChangePct24h) < 0 && '-') || ''}${data.priceChangePct24hDisplay}`}
                    </div>
                  </div>
                  {:else}
                  <div class="row df aic jcsb ph24 cp" on:click={tradeClickHandler(data)}>
                    <div class="df aic jcfs fw2 f1 lh34 cell-val-pair">
                      <img class="mr12" width="32" height="32" src={coinUtil.getUrl(data.baseCurrency)} alt={data.displaySymbol} />
                      {#if contractUtil.isContract(data.type)}
                        <div>
                          <div class="lh20 fw2 ooo">
                            <span>{data.symbolV2}</span>
                            <span class="cell-type xs:dn">{LG(lang.Perpetual)}</span></div>
                          <div class="lh20 f12 T4">{data.settleCurrencyDisplay}-{LG(lang.Margin)}</div>
                        </div>
                      {:else}
                        {data.displaySymbol}
                      {/if}
                    </div>
                    <div class="cell-124 lh34 cell-val-close">${numUtil.delimit(data.close || 0)}</div>
                    <div class="cell-124 lh34 cell-val-changepercent">
                  <span class:up={data.changePercent > 0} class:down={data.changePercent < 0} class="br4 lh34 cell-124">
                    {data.changePercentStr}
                  </span>
                    </div>
                    <div class="df aic cell-124 lh34 xs:dn">
                      <TrendImage width={100} height={22} data={data.klineData} change={data.change24hEp} />
                    </div>
                    <div class="tr cell-110 xs:dn">
                      <Button class="trade wsn fw2" newRadius type="common" size="small">{LG(lang.Trade)}</Button>
                    </div>
                  </div>
                {/if}

              {/each}
            {/if}
          </div>
        </SkeletonBasicView>
        <div class="h5-more mt16 tc f14 dn xs:db">
          <Button type="text" on:click={viewMoreClickHandler}>View More <i class="iconfont arrow dib fw3">&#xe623;</i></Button>
        </div>
      </div>
      <div class="border-base right f16 xs:dn">
        {#each more as m}
          <div class="more-wrap pv24">
            <div class="title-wrap df aic jcsb ph24">
              <h3 class="f16 fw3 label">{m.label}</h3>
              <a href={m.href} class="c-gray f14" rel="noopener noreferrer nofollow"><i class="iconfont arrow dib fw3">&#xe623;</i></a>
            </div>
            <div>
            <SkeletonBasicView loaded={m.cryptos && m.cryptos.length === MORE_DATA_LIST_COUNT}>
              <svelte:fragment slot="skeleton">
                <div class="df fdcr ml24 mr24">
                  {#each new Array(MORE_DATA_LIST_COUNT).fill(0) as item}
                    <SkeletonLine class="mt12" height="64px" childProps={[
                      {width: '32px', height: '32px', borderRadius: '50%', baseColor: '#333', highlightColor: '#090a0a'},
                      {width: '200px', baseColor: '#333', highlightColor: '#090a0a'},
                      {width: '60px', class: 'grid-end', baseColor: '#333', highlightColor: '#090a0a'},
                    ]}></SkeletonLine>
                  {/each}
                </div>
              </svelte:fragment>
              <div class="df jcsb c-gray fw1 f12 ph20 mt12 table-header">
                <span class="cell-pair">{LG(lang.TradingPairs)}</span>
                <span class="f1">{LG(lang.LastPrice)}</span>
                <span class="tr cell-change-percent">{LG(lang.Change24H)}</span>
              </div>
              {#each m.cryptos as crypto}
                <div class="crypto df aic jcsb ph24 cp f14" on:click={tradeClickHandler(crypto)}>
                  <div class="df aic cell-pair lh34">
                    <div class="df aic jcc ovh img-wrap">
                      <img width="32" height="32" src={crypto.logoUrl || ''} onerror="this.src='https://img.phemex.com/v1/a4afa886-3400-4962-859d-bba075cc0570/defult-coin.svg'" alt={crypto.fullNamefullName} />
                    </div>
                    <div class="f14 fw2 ml12">{crypto.coin}</div>
                  </div>
                  <div class="f1 tl lh34">${numUtil.delimit(crypto.price || 0)}</div>
                  <div class="lh34 tr cell-change-percent" class:up={Number(crypto.priceChangePct24h) > 0} class:down={Number(crypto.priceChangePct24h) < 0} >
                    {`${(Number(crypto.priceChangePct24h) > 0 && '+') || (Number(crypto.priceChangePct24h) < 0 && '-') || ''}${crypto.priceChangePct24hDisplay}`}
                  </div>
                </div>
              {/each}
            </SkeletonBasicView>
            </div>
          </div>
        {/each}
      </div>
    </div>
  </div>
</section>
